import React, { useCallback, useEffect, useState } from 'react';
import { CalendarOff, CheckCircle, ChevronLeft, ChevronRight, Paperclip, Plus, RefreshCw, XCircle } from 'lucide-react';
import { attendanceAPI } from '../../services/api';
import { useToast } from '../UIProvider';
import { authedMediaUrl } from '../../utils/media';
import { clinicToday, formatClinicDateTime } from '../../utils/clinicTime';
import { LEAVE_STATUS, datePart, formatDays } from './attendanceStatus';
import { describeRange } from './MyLeaveCard';
import LeaveRequestDialog from './LeaveRequestDialog';

// Leave for whoever approves it: the requests waiting on a decision, and a month calendar of who is
// already off, so two people are not approved for the same days by accident.

const monthKey = (date) => String(date).slice(0, 7);
const shiftMonth = (ym, by) => {
  const [y, m] = ym.split('-').map(Number);
  const d = new Date(Date.UTC(y, m - 1 + by, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
};

const RequestCard = ({ request, canDecide, onDecided }) => {
  const notify = useToast();
  const [rejecting, setRejecting] = useState(false);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const chip = LEAVE_STATUS[request.status] || LEAVE_STATUS.PENDING;

  const decide = async (decision) => {
    if (decision === 'REJECTED' && !note.trim()) { setError('Say why the leave is refused.'); return; }
    setSaving(true);
    setError('');
    try {
      const saved = await attendanceAPI.decideLeave(request.id, decision, note.trim() || null);
      notify('success', decision === 'APPROVED' ? 'Leave approved' : 'Leave refused');
      onDecided(saved);
    } catch (e) {
      setError(e?.message || 'The decision could not be saved.');
      setSaving(false);
    }
  };

  return (
    <li className="bg-white rounded-2xl shadow-xl p-4 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-semibold text-gray-900">{request.userName}</p>
          <p className="text-sm text-gray-700">
            {request.typeName}
            {request.paid === false && <span className="text-gray-500"> (unpaid)</span>}
            {' · '}{describeRange(request)}{' · '}{formatDays(request.days)}
          </p>
          <p className="text-sm text-gray-600 mt-1">{request.reason}</p>
          <p className="text-xs text-gray-400 mt-1">Asked on {formatClinicDateTime(request.createdAt)}</p>
          {request.attachmentUrl && (
            <a href={authedMediaUrl(request.attachmentUrl)} target="_blank" rel="noreferrer" className="text-xs text-navy-700 hover:underline inline-flex items-center gap-1 mt-1">
              <Paperclip className="w-3 h-3" aria-hidden="true" /> Attachment
            </a>
          )}
        </div>
        <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold flex-shrink-0 ${chip.chip}`}>{chip.label}</span>
      </div>

      {request.status !== 'PENDING' && request.decidedByName && (
        <p className="text-xs text-gray-600">
          {chip.label} by {request.decidedByName} on {formatClinicDateTime(request.decidedAt)}
          {request.decisionNote && <span className="block text-gray-800">“{request.decisionNote}”</span>}
        </p>
      )}

      {request.status === 'PENDING' && canDecide && (
        <div className="space-y-2">
          {rejecting && (
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              autoFocus
              aria-label={`Reason for refusing ${request.userName}'s leave`}
              placeholder="Why the leave is refused"
              className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 text-sm"
            />
          )}
          {error && <p className="text-sm text-red-700">{error}</p>}
          <div className="flex gap-2">
            {rejecting ? (
              <>
                <button type="button" onClick={() => { setRejecting(false); setError(''); }} disabled={saving} className="flex-1 px-4 py-2 border border-gray-300 bg-white text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50">Back</button>
                <button type="button" onClick={() => decide('REJECTED')} disabled={saving} className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium disabled:opacity-50 flex items-center justify-center gap-1.5">
                  <XCircle className="w-4 h-4" aria-hidden="true" /> Refuse
                </button>
              </>
            ) : (
              <>
                <button type="button" onClick={() => setRejecting(true)} disabled={saving} className="flex-1 px-4 py-2 border border-red-300 bg-white text-red-700 rounded-lg text-sm font-medium hover:bg-red-50 flex items-center justify-center gap-1.5">
                  <XCircle className="w-4 h-4" aria-hidden="true" /> Refuse
                </button>
                <button type="button" onClick={() => decide('APPROVED')} disabled={saving} className="flex-1 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-medium disabled:opacity-50 flex items-center justify-center gap-1.5">
                  <CheckCircle className="w-4 h-4" aria-hidden="true" /> Approve
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </li>
  );
};

const WhosOff = ({ month, onMonth }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let live = true;
    setData(null);
    attendanceAPI.leaveCalendar(month)
      .then((res) => { if (live) { setData(res); setError(null); } })
      .catch((e) => { if (live) setError(e?.message || 'The calendar could not be loaded.'); });
    return () => { live = false; };
  }, [month]);

  const days = (data?.days || []).filter((d) => d.holiday || d.people?.length);

  return (
    <div className="bg-white rounded-2xl shadow-xl p-5 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <h3 className="font-semibold text-gray-900">Who is off</h3>
        <div className="flex items-center gap-1">
          <button type="button" aria-label="Previous month" onClick={() => onMonth(shiftMonth(month, -1))} className="p-1.5 rounded-lg border border-gray-200 hover:bg-gray-50">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="text-sm font-medium text-gray-700 min-w-[8rem] text-center">
            {datePart(`${month}-01`, { month: 'long', year: 'numeric' })}
          </span>
          <button type="button" aria-label="Next month" onClick={() => onMonth(shiftMonth(month, 1))} className="p-1.5 rounded-lg border border-gray-200 hover:bg-gray-50">
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {error ? (
        <p className="text-sm text-red-700">{error}</p>
      ) : !data ? (
        <p className="text-sm text-gray-500 flex items-center gap-2"><RefreshCw className="w-4 h-4 animate-spin" aria-hidden="true" /> Loading…</p>
      ) : days.length === 0 ? (
        <p className="text-sm text-gray-500">Nobody is off this month, and there are no holidays.</p>
      ) : (
        <ul className="divide-y divide-gray-100">
          {days.map((d) => (
            <li key={d.date} className="py-2 flex items-start gap-3 text-sm">
              <span className="w-20 flex-shrink-0 text-gray-500">{datePart(d.date, { weekday: 'short', day: 'numeric', month: 'short' })}</span>
              <span className="flex-1 min-w-0">
                {d.holiday && <span className="inline-flex rounded-full bg-teal-100 text-teal-800 px-2 py-0.5 text-xs font-semibold mr-1.5">{d.holiday}</span>}
                {(d.people || []).map((p) => (
                  <span key={`${p.userId}-${d.date}`} className="inline-flex items-center rounded-full bg-navy-100 text-navy-800 px-2 py-0.5 text-xs font-medium mr-1.5 mb-1">
                    {p.userName}
                    <span className="text-navy-600 font-normal ml-1">
                      {p.typeName}{p.half ? ', half' : ''}{p.paid === false ? ', unpaid' : ''}
                    </span>
                  </span>
                ))}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const LeaveTab = ({ canDecide, reloadKey }) => {
  const [filter, setFilter] = useState('PENDING');
  const [requests, setRequests] = useState(null);
  const [error, setError] = useState(null);
  const [month, setMonth] = useState(monthKey(clinicToday()));
  const [adding, setAdding] = useState(false);
  const [localKey, setLocalKey] = useState(0);

  const load = useCallback(async () => {
    setRequests(null);
    try {
      const rows = await attendanceAPI.leaveRequests(filter === 'ALL' ? {} : { status: filter });
      setRequests(Array.isArray(rows) ? rows : []);
      setError(null);
    } catch (e) {
      setError(e?.message || 'The leave requests could not be loaded.');
    }
  }, [filter]);
  useEffect(() => { load(); }, [load, reloadKey, localKey]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex gap-2" role="group" aria-label="Which requests">
          {[['PENDING', 'Waiting'], ['APPROVED', 'Approved'], ['REJECTED', 'Refused'], ['ALL', 'All']].map(([v, label]) => (
            <button
              key={v}
              type="button"
              aria-pressed={filter === v}
              onClick={() => setFilter(v)}
              className={`px-4 py-2 rounded-lg text-sm font-medium ${filter === v ? 'bg-navy-800 text-white' : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'}`}
            >
              {label}
            </button>
          ))}
        </div>
        {canDecide && (
          <button type="button" onClick={() => setAdding(true)} className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700">
            <Plus className="w-4 h-4" aria-hidden="true" /> Record leave for someone
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
        <div>
          {error ? (
            <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-lg text-sm text-red-800">{error}</div>
          ) : !requests ? (
            <div className="bg-white rounded-2xl shadow-xl p-10 text-center">
              <RefreshCw className="h-6 w-6 text-emerald-500 animate-spin mx-auto mb-2" />
              <p className="text-sm text-gray-500">Loading leave…</p>
            </div>
          ) : requests.length === 0 ? (
            <div className="bg-white rounded-2xl shadow-xl p-10 text-center text-sm text-gray-500">
              <CalendarOff className="w-6 h-6 text-gray-300 mx-auto mb-2" aria-hidden="true" />
              {filter === 'PENDING' ? 'No leave is waiting for a decision.' : 'Nothing here.'}
            </div>
          ) : (
            <ul className="space-y-3">
              {requests.map((r) => (
                <RequestCard key={r.id} request={r} canDecide={canDecide} onDecided={() => setLocalKey((n) => n + 1)} />
              ))}
            </ul>
          )}
        </div>
        <WhosOff month={month} onMonth={setMonth} />
      </div>

      {adding && (
        <LeaveRequestDialog
          forUser
          onClose={() => setAdding(false)}
          onSaved={() => { setAdding(false); setLocalKey((n) => n + 1); }}
        />
      )}
    </div>
  );
};

export default LeaveTab;
